let campaignConfigCache;

export function getCampaignConfig() {
    if (campaignConfigCache !== undefined) {
        return campaignConfigCache;
    }

    const configEl = document.getElementById("atlas-campaign-competition-config");
    if (!configEl) {
        campaignConfigCache = null;
        return campaignConfigCache;
    }

    try {
        const parsed = JSON.parse(configEl.textContent);
        campaignConfigCache = parsed && parsed.active ? parsed : null;
    } catch (error) {
        console.error("Campaign config parse error:", error);
        campaignConfigCache = null;
    }

    return campaignConfigCache;
}

export function isSurgeActive(config, nowMs = Date.now()) {
    if (!config?.surge_enabled || !config?.surge_start || !config?.surge_end) {
        return false;
    }

    const surgeStartMs = new Date(config.surge_start).getTime();
    const surgeEndMs = new Date(config.surge_end).getTime();
    if (!Number.isFinite(surgeStartMs) || !Number.isFinite(surgeEndMs)) {
        return false;
    }

    return nowMs >= surgeStartMs && nowMs <= surgeEndMs;
}

export function resolveSurgeBonus(config, kind, nowMs = Date.now()) {
    if (!isSurgeActive(config, nowMs)) {
        return 0;
    }

    const surgeMultiplier = parseInt(config?.surge_multiplier, 10) || 0;
    if (surgeMultiplier <= 0) {
        return 0;
    }

    const appliesTo = config?.surge_applies_to || "Both";
    if (appliesTo === "Both") {
        return surgeMultiplier;
    }
    if (appliesTo === "One time" && kind === "spend") {
        return surgeMultiplier;
    }
    if (appliesTo === "Subscription" && kind === "subscription") {
        return surgeMultiplier;
    }

    return 0;
}

export function entriesFromSpend(spendCents, thresholdCents, roundingRule) {
    const spend = Number(spendCents) || 0;
    const threshold = Number(thresholdCents) || 0;
    if (spend <= 0 || threshold <= 0) {
        return 0;
    }

    const rawFloat = spend / threshold;
    if (roundingRule === "ceil") {
        return Math.ceil(rawFloat);
    }
    if (roundingRule === "round") {
        return Math.round(rawFloat);
    }
    return Math.floor(rawFloat);
}

export function getWallClockTime(dateStr, isNow, timeZone) {
    try {
        if (isNow) {
            const nowStr = new Date().toLocaleString("en-US", {
                timeZone,
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false
            });
            return new Date(nowStr).getTime();
        }

        if (!dateStr) {
            return 0;
        }

        const cleanStr = dateStr
            .replace("Z", "")
            .replace(/-/g, "/")
            .replace("T", " ");
        return new Date(cleanStr).getTime();
    } catch (error) {
        console.warn("Timezone parsing fallback:", error);
        return isNow ? Date.now() : new Date(dateStr).getTime();
    }
}

// Debounce that exposes cancel() for cleanup on disconnect
function debounce(fn, ms = 150) {
    let timer = null;
    const debounced = (...args) => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
            timer = null;
            fn(...args);
        }, ms);
    };
    debounced.cancel = () => {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
    };
    return debounced;
}

// Shared cart snapshot + change event so PDP can stay in sync with cart totals
export const CART_STATE_EVENT = "atlas-campaign:cart-state";
let cartStateSnapshot = null;
let cartStateFetchPromise = null;

export function getCartState() {
    return cartStateSnapshot;
}

function setCartState(next) {
    cartStateSnapshot = next;
    document.dispatchEvent(
        new CustomEvent(CART_STATE_EVENT, { detail: next, bubbles: true })
    );
}

// Build a cart snapshot from a /cart.js-shaped object using the campaign config
function deriveCartState(cart, config) {
    const productVariantId = config?.product_id
        ? parseInt(config.product_id, 10)
        : null;

    let spendCents = 0;
    let hasSubscription = false;
    const items = Array.isArray(cart?.items) ? cart.items : [];

    items.forEach((item) => {
        if (productVariantId && item.variant_id === productVariantId) {
            return;
        }
        if (item.price > 0) {
            spendCents += item.line_price;
        }
        if (item.selling_plan_allocation?.selling_plan?.id) {
            hasSubscription = true;
        }
    });

    const thresholdCents = (Number(config?.spend_threshold) || 0) * 100;
    const baseEntries = entriesFromSpend(
        spendCents,
        thresholdCents,
        config?.rounding_rule
    );
    const cartItemCount = items.reduce((acc, item) => acc + item.quantity, 0);

    return {
        hasSubscription,
        hasSpendEntries: baseEntries > 0,
        spendCents,
        cartItemCount,
        surgeBonusSubscriptionApplied:
            hasSubscription && resolveSurgeBonus(config, "subscription") > 0,
        surgeBonusSpendApplied:
            baseEntries > 0 && resolveSurgeBonus(config, "spend") > 0,
        subscriptionBonusApplied:
            hasSubscription && !!config?.subscription_bonus_enabled
    };
}

// Fallback snapshot fetch used by PDP when cart custom element has not published one yet
async function ensureCartState() {
    if (cartStateSnapshot) return cartStateSnapshot;
    if (cartStateFetchPromise) return cartStateFetchPromise;

    const config = getCampaignConfig();
    if (!config) return null;

    cartStateFetchPromise = fetch("/cart.js", { credentials: "same-origin" })
        .then((res) => res.json())
        .then((cart) => {
            const state = deriveCartState(cart, config);
            setCartState(state);
            return state;
        })
        .catch((error) => {
            console.warn("Campaign cart.js fetch failed:", error);
            return null;
        })
        .finally(() => {
            cartStateFetchPromise = null;
        });

    return cartStateFetchPromise;
}

class AtlasCampaignTimer extends HTMLElement {
    constructor() {
        super();
        this.daysEl = this.querySelector("[data-timer-days]");
        this.hoursEl = this.querySelector("[data-timer-hours]");
        this.minutesEl = this.querySelector("[data-timer-minutes]");
        this.secondsEl = this.querySelector("[data-timer-seconds]");
        this.interval = null;
        this.timeZone = "Pacific/Auckland";
        this.targetDateStr = null;
        this.displayMode = "Always";
    }

    connectedCallback() {
        this.initTimer();
    }

    disconnectedCallback() {
        if (this.interval) {
            clearInterval(this.interval);
            this.interval = null;
        }
    }

    initTimer() {
        const config = getCampaignConfig();
        if (!config || !config.show_countdown_timer) {
            return;
        }

        this.timeZone = config.timezone || "Pacific/Auckland";

        let targetDateStr = config.end_date;
        let titleText =
            config.countdown_campaign_text !== undefined
                ? config.countdown_campaign_text
                : "CAMPAIGN ENDS IN:";

        if (config.surge_enabled && config.surge_end && config.surge_start) {
            const surgeStart = getWallClockTime(
                config.surge_start,
                false,
                this.timeZone
            );
            const surgeEnd = getWallClockTime(
                config.surge_end,
                false,
                this.timeZone
            );
            const now = getWallClockTime(null, true, this.timeZone);

            if (now >= surgeStart && now < surgeEnd) {
                targetDateStr = config.surge_end;
                titleText =
                    config.countdown_campaign_surge_text !== undefined
                        ? config.countdown_campaign_surge_text
                        : "SURGE ENDS IN:";
            }
        }

        if (!targetDateStr) {
            return;
        }

        const titleEl = this.querySelector(".campaign-timer__title");
        if (titleEl) {
            if (titleText === null || titleText.trim() === "") {
                titleEl.style.display = "none";
            } else {
                titleEl.textContent = titleText;
                titleEl.style.display = "block";
            }
        }

        this.targetDateStr = targetDateStr;
        this.displayMode = config.countdown_display_mode || "Always";

        this.updateTimer();
        this.interval = setInterval(() => this.updateTimer(), 1000);
    }

    updateTimer() {
        const now = getWallClockTime(null, true, this.timeZone);
        const targetDate = getWallClockTime(this.targetDateStr, false, this.timeZone);
        const distance = targetDate - now;

        if (distance < 0) {
            if (this.interval) {
                clearInterval(this.interval);
                this.interval = null;
            }
            this.remove();
            return;
        }

        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
            (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        const daysBlock = this.daysEl?.closest(".campaign-timer__block");
        const hoursBlock = this.hoursEl?.closest(".campaign-timer__block");
        const minutesBlock = this.minutesEl?.closest(".campaign-timer__block");

        if (daysBlock) {
            daysBlock.style.display = days > 0 ? "" : "none";
        }
        if (hoursBlock) {
            hoursBlock.style.display = days > 0 || hours > 0 ? "" : "none";
        }
        if (minutesBlock) {
            minutesBlock.style.display =
                days > 0 || hours > 0 || minutes > 0 ? "" : "none";
        }

        let shouldShow = false;
        if (this.displayMode === "Always") {
            shouldShow = true;
        } else if (
            this.displayMode === "Last 7 days" &&
            distance <= 7 * 24 * 60 * 60 * 1000
        ) {
            shouldShow = true;
        } else if (
            this.displayMode === "Last 3 days" &&
            distance <= 3 * 24 * 60 * 60 * 1000
        ) {
            shouldShow = true;
        } else if (
            this.displayMode === "Last day" &&
            distance <= 24 * 60 * 60 * 1000
        ) {
            shouldShow = true;
        }

        this.classList.toggle("is-active", shouldShow);

        if (this.daysEl) {
            this.daysEl.textContent = days.toString().padStart(2, "0");
        }
        if (this.hoursEl) {
            this.hoursEl.textContent = hours.toString().padStart(2, "0");
        }
        if (this.minutesEl) {
            this.minutesEl.textContent = minutes.toString().padStart(2, "0");
        }
        if (this.secondsEl) {
            this.secondsEl.textContent = seconds.toString().padStart(2, "0");
        }
    }
}

class AtlasCampaignPdpEntries extends HTMLElement {
    constructor() {
        super();
        this.countEl = null;
        this.variantsData = [];
        this.config = null;
        this.currentVariantId = null;
        this.scope = null;
        this.cartState = null;
        this.boundHandleMessage = this.handleMessage.bind(this);
        this.boundHandleChange = this.handleChange.bind(this);
        this.boundHandlePopState = this.updateFromUrl.bind(this);
        this.boundCartState = this.handleCartState.bind(this);
        this.originalPushState = null;
    }

    connectedCallback() {
        const variantsNode = this.querySelector("[data-product-variants]");
        if (variantsNode) {
            try {
                this.variantsData = JSON.parse(variantsNode.textContent);
            } catch (error) {
                console.error("Error parsing product variants JSON:", error);
            }
        }

        this.countEl = this.querySelector("[data-entries-count]");

        this.config = getCampaignConfig();
        if (!this.config) {
            this.classList.add("is-empty");
            return;
        }


        this.scope = this.closest("product-container") || document;

        this.cartState = getCartState();

        this.classList.add("is-empty");

        window.addEventListener("message", this.boundHandleMessage);
        document.addEventListener("change", this.boundHandleChange);
        window.addEventListener("popstate", this.boundHandlePopState);
        document.addEventListener(CART_STATE_EVENT, this.boundCartState);

        this.originalPushState = history.pushState;
        history.pushState = (...args) => {
            this.originalPushState.apply(history, args);
            this.updateFromUrl();
        };

        this.updateFromUrl();
        this.classList.add("is-initialized");

        if (!this.cartState) {
            ensureCartState();
        }
    }

    disconnectedCallback() {
        window.removeEventListener("message", this.boundHandleMessage);
        document.removeEventListener("change", this.boundHandleChange);
        window.removeEventListener("popstate", this.boundHandlePopState);
        document.removeEventListener(CART_STATE_EVENT, this.boundCartState);

        if (this.originalPushState) {
            history.pushState = this.originalPushState;
        }
    }

    handleCartState(event) {
        this.cartState = event?.detail || getCartState();
        this.calculateEntries();
    }

    updateFromUrl() {
        const urlParams = new URLSearchParams(window.location.search);
        const variantId = urlParams.get("variant");
        if (variantId && parseInt(variantId, 10) !== this.currentVariantId) {
            this.currentVariantId = parseInt(variantId, 10);
            this.calculateEntries();
            return;
        }

        if (!variantId && this.variantsData.length > 0) {
            this.currentVariantId = this.variantsData[0].id;
            this.calculateEntries();
        }
    }

    handleChange(event) {
        const target = event.target;
        if (this.scope && this.scope !== document && !this.scope.contains(target)) {
            return;
        }

        if (
            target.name === "id" &&
            target.form &&
            target.form.action.includes("/cart/add")
        ) {
            this.currentVariantId = parseInt(target.value, 10);
            this.calculateEntries();
        } else if (target.name === "product_variants") {
            const variantId = target.getAttribute("data-variant-id");
            if (variantId) {
                this.currentVariantId = parseInt(variantId, 10);
            }
            this.calculateEntries();
        } else if (target.name === "product-variants-toggle") {
            setTimeout(() => this.calculateEntries(), 50);
        } else if (
            target.name === "selling_plan" ||
            target.closest('[name="selling_plan"]') ||
            target.closest('form[action*="/cart/add"]')
        ) {
            setTimeout(() => this.calculateEntries(), 50);
        }
    }

    handleMessage(event) {
        if (
            event.data &&
            event.data.type === "variantChange" &&
            event.data.variant
        ) {
            this.currentVariantId = event.data.variant.id;
            this.calculateEntries();
        }
    }

    isSubscriptionSelected() {
        const scope = this.scope || document;
        let isSub = false;

        const planInputs = scope.querySelectorAll(
            'input[name="selling_plan"], select[name="selling_plan"]'
        );
        planInputs.forEach((input) => {
            if (input.type === "radio" || input.type === "checkbox") {
                if (input.checked && input.value && input.value !== "0") {
                    isSub = true;
                }
            } else if (input.value && input.value !== "0" && input.value !== "") {
                isSub = true;
            }
        });

        if (!isSub) {
            const toggleInput = scope.querySelector(
                'input[name="product-variants-toggle"]:checked'
            );
            if (toggleInput && toggleInput.value === "subscription") {
                isSub = true;
            }

            const customPlanInput = scope.querySelector(
                'input[name="product_variants"]:checked'
            );
            if (customPlanInput) {
                const planId = customPlanInput.getAttribute(
                    "data-subscription-plan-id"
                );
                if (planId && planId !== "0" && planId !== "") {
                    isSub = true;
                }
            }
        }

        return isSub;
    }

    getSelectedPlanId() {
        const scope = this.scope || document;
        let planId = null;

        const planInputs = scope.querySelectorAll(
            'input[name="selling_plan"], select[name="selling_plan"]'
        );
        planInputs.forEach((input) => {
            if (input.type === "radio" || input.type === "checkbox") {
                if (input.checked && input.value && input.value !== "0") {
                    planId = input.value;
                }
            } else if (input.value && input.value !== "0" && input.value !== "") {
                planId = input.value;
            }
        });

        if (!planId) {
            const customPlanInput = scope.querySelector(
                'input[name="product_variants"]:checked'
            );
            if (customPlanInput) {
                const pid = customPlanInput.getAttribute("data-subscription-plan-id");
                if (pid && pid !== "0" && pid !== "") {
                    planId = pid;
                }
            }
        }

        return planId ? parseInt(planId, 10) : null;
    }

    calculateEntries() {
        if (!this.config?.active || !this.currentVariantId) {
            this.classList.toggle("is-empty", true);
            return;
        }

        const variant = this.variantsData.find((v) => v.id === this.currentVariantId);
        if (!variant) {
            this.classList.toggle("is-empty", true);
            return;
        }

        const isSub = this.isSubscriptionSelected();
        let priceCents = Number(variant.price) || 0;

        if (isSub && variant.selling_plan_allocations && variant.selling_plan_allocations.length > 0) {
            const planId = this.getSelectedPlanId();
            const allocation = variant.selling_plan_allocations.find(a => a.plan_id === planId);
            if (allocation) {
                priceCents = Number(allocation.price) || priceCents;
            } else {
                priceCents = Number(variant.selling_plan_allocations[0].price) || priceCents;
            }
        }
        const thresholdCents = (Number(this.config.spend_threshold) || 40) * 100;
        const baseEntries = entriesFromSpend(
            priceCents,
            thresholdCents,
            this.config.rounding_rule || "floor"
        );

        const state = this.cartState || {};
        // Bonuses below are "once per order" — do not duplicate what cart already has
        const spendSurgeAlreadyApplied = !!state.surgeBonusSpendApplied;
        const subSurgeAlreadyApplied = !!state.surgeBonusSubscriptionApplied;
        const subBonusAlreadyApplied = !!state.subscriptionBonusApplied;

        let extra = 0;

        if (baseEntries > 0 && !spendSurgeAlreadyApplied) {
            extra += resolveSurgeBonus(this.config, "spend");
        }
        if (isSub && !subSurgeAlreadyApplied) {
            extra += resolveSurgeBonus(this.config, "subscription");
        }
        if (
            isSub &&
            this.config.subscription_bonus_enabled &&
            !subBonusAlreadyApplied
        ) {
            extra += parseInt(this.config.subscription_bonus_entries, 10) || 1;
        }

        const totalEntries = baseEntries + extra;

        if (this.countEl) {
            this.countEl.textContent = String(totalEntries);
        }
        this.classList.toggle("is-empty", totalEntries <= 0);
    }
}

class AtlasCampaignCart extends HTMLElement {
    constructor() {
        super();
        this.config = null;
        this.isUpdatingCart = false;
        // PUT guard trackers: last attempted qty and last server-capped qty (stock ceiling)
        this.lastRequestedBalls = null;
        this.lastServerCapForVariant = null;
        this.pendingEvent = null;
        this.scheduleRecalc = debounce(() => this.recalc(), 150);
        this.boundRebuyEvent = (event) => {
            this.pendingEvent = event;
            this.scheduleRecalc();
        };
    }

    connectedCallback() {
        this.config = getCampaignConfig();
        if (!this.config) {
            return;
        }

        document.addEventListener("rebuy:cart.ready", this.boundRebuyEvent);
        document.addEventListener("rebuy:cart.change", this.boundRebuyEvent);
    }

    disconnectedCallback() {
        document.removeEventListener("rebuy:cart.ready", this.boundRebuyEvent);
        document.removeEventListener("rebuy:cart.change", this.boundRebuyEvent);
        if (this.scheduleRecalc && typeof this.scheduleRecalc.cancel === "function") {
            this.scheduleRecalc.cancel();
        }
        this.pendingEvent = null;
    }

    recalc() {
        this.injectCampaignElementsIntoRebuy();
        this.calculateCampaignEntries(this.pendingEvent);
        this.pendingEvent = null;
    }

    async updateCartEntries(variantId, quantity) {
        if (this.isUpdatingCart) {
            return;
        }
        this.isUpdatingCart = true;

        try {
            const response = await fetch("/cart/update.js", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ updates: { [variantId]: quantity } })
            });
            const cart = await response.json();


            const line = Array.isArray(cart?.items)
                ? cart.items.find((item) => item.variant_id === variantId)
                : null;
            const actualQty = line ? Number(line.quantity) || 0 : 0;
            if (actualQty < quantity) {
                this.lastServerCapForVariant = actualQty;
            } else {
                this.lastServerCapForVariant = null;
            }

            if (window.Rebuy) {
                if (typeof window.Rebuy.fetchCart === "function") {
                    window.Rebuy.fetchCart();
                } else if (
                    window.Rebuy.Cart &&
                    typeof window.Rebuy.Cart.fetch === "function"
                ) {
                    window.Rebuy.Cart.fetch();
                } else if (
                    window.Rebuy.SmartCart &&
                    typeof window.Rebuy.SmartCart.fetch === "function"
                ) {
                    window.Rebuy.SmartCart.fetch();
                } else {
                    document.dispatchEvent(
                        new CustomEvent("cart:updated", {
                            bubbles: true,
                            detail: { cart }
                        })
                    );
                }
            }
        } catch (error) {
            console.error("Error updating campaign entries in cart:", error);
        } finally {
            this.isUpdatingCart = false;
        }
    }

    resolveCartItems(event) {
        let cartItems = [];

        if (event?.detail?.cart) {
            if (Array.isArray(event.detail.cart.items)) {
                cartItems = event.detail.cart.items;
            } else if (typeof event.detail.cart.items === "function") {
                cartItems = event.detail.cart.items();
            } else if (Array.isArray(event.detail.cart)) {
                cartItems = event.detail.cart;
            }
        }

        if ((!Array.isArray(cartItems) || cartItems.length === 0) && window.Rebuy?.SmartCart) {
            const rebuyItems =
                typeof window.Rebuy.SmartCart.items === "function"
                    ? window.Rebuy.SmartCart.items()
                    : window.Rebuy.SmartCart.items;

            if (Array.isArray(rebuyItems)) {
                cartItems = rebuyItems;
            } else if (rebuyItems && Array.isArray(rebuyItems.items)) {
                cartItems = rebuyItems.items;
            }
        }

        return Array.isArray(cartItems) ? cartItems : [];
    }

    calculateCampaignEntries(event) {
        const cartItems = this.resolveCartItems(event);
        if (!Array.isArray(cartItems)) {
            return;
        }

        let spendCents = 0;
        let hasSubscription = false;
        let currentEntryQty = 0;
        const productVariantId = this.config.product_id
            ? parseInt(this.config.product_id, 10)
            : null;

        cartItems.forEach((item) => {
            if (productVariantId && item.variant_id === productVariantId) {
                currentEntryQty = item.quantity;
            } else {
                if (item.price > 0) {
                    spendCents += item.line_price;
                }
                if (item.selling_plan_allocation?.selling_plan?.id) {
                    hasSubscription = true;
                }
            }
        });

        const thresholdCents = (Number(this.config.spend_threshold) || 0) * 100;
        const baseEntries = entriesFromSpend(
            spendCents,
            thresholdCents,
            this.config.rounding_rule
        );

        let subBonus = 0;
        let baseSubBonus = 0;
        if (hasSubscription && this.config.subscription_bonus_enabled) {
            subBonus = this.config.subscription_bonus_entries
                ? parseInt(this.config.subscription_bonus_entries, 10)
                : 1;
            baseSubBonus = subBonus;
        }

        const baseEntriesForBalls = baseEntries;
        let spendPart = baseEntries;
        if (baseEntries > 0) {
            spendPart += resolveSurgeBonus(this.config, "spend");
        }

        let subPart = subBonus;
        if (hasSubscription) {
            subPart += resolveSurgeBonus(this.config, "subscription");
        }

        const totalEntries = spendPart + subPart;
        const totalBalls = baseEntriesForBalls + baseSubBonus;

        let neededCents = 0;
        let progressPercent = 0;
        if (thresholdCents > 0) {
            const remainderCents = spendCents % thresholdCents;
            neededCents = thresholdCents - remainderCents;
            progressPercent = Math.min(100, (remainderCents / thresholdCents) * 100);
        }

        this.updateCampaignDOM(totalEntries, neededCents, progressPercent, hasSubscription);

        const cartItemCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);
        this.updateCartHeaderBubble(cartItemCount);

        // Guard against POST /cart/update.js loop:
        // - skip if already issued a PUT for this totalBalls (same intent)
        // - skip if server previously capped this variant below requested qty (stock cap)
        const shouldUpdate =
            productVariantId &&
            currentEntryQty !== totalBalls &&
            totalBalls !== this.lastRequestedBalls &&
            !(
                this.lastServerCapForVariant !== null &&
                totalBalls > this.lastServerCapForVariant
            );

        if (shouldUpdate) {
            this.lastRequestedBalls = totalBalls;
            this.updateCartEntries(productVariantId, totalBalls);
        }

        // Publish cart state so PDP can display correct "additional entries"
        // without duplicating once-per-order bonuses already in cart.
        setCartState({
            hasSubscription,
            hasSpendEntries: baseEntries > 0,
            spendCents,
            cartItemCount,
            surgeBonusSubscriptionApplied:
                hasSubscription && resolveSurgeBonus(this.config, "subscription") > 0,
            surgeBonusSpendApplied:
                baseEntries > 0 && resolveSurgeBonus(this.config, "spend") > 0,
            subscriptionBonusApplied:
                hasSubscription && !!this.config.subscription_bonus_enabled
        });
    }

    updateCampaignDOM(totalEntries, neededCents, progressPercent, hasSubscription) {
        document.querySelectorAll("[data-campaign-total-entries]").forEach((el) => {
            el.textContent = totalEntries;
        });

        document
            .querySelectorAll(".campaign-announcement-bar, .campaign-cart-reward")
            .forEach((el) => {
                el.setAttribute("data-has-entries", totalEntries > 0 ? "true" : "false");
            });

        document.querySelectorAll("[data-campaign-spend-needed]").forEach((el) => {
            el.textContent = (neededCents / 100).toFixed(2);
        });

        document.querySelectorAll("[data-campaign-progress-bar]").forEach((el) => {
            el.style.width = `${progressPercent}%`;
        });

        document.querySelectorAll("[data-campaign-sub-message]").forEach((el) => {
            el.style.display = hasSubscription ? "none" : "block";
        });

        document.querySelectorAll("[data-campaign-sub-success]").forEach((el) => {
            el.style.display = hasSubscription ? "block" : "none";
        });
    }

    updateCartHeaderBubble(itemCount) {
        document.querySelectorAll(".cart-count-bubble").forEach((bubble) => {
            let countSpan = bubble.querySelector("span[aria-hidden='true']");
            const visuallyHiddenSpan = bubble.querySelector(".visually-hidden");

            if (itemCount < 100) {
                if (!countSpan) {
                    countSpan = document.createElement("span");
                    countSpan.setAttribute("aria-hidden", "true");
                    bubble.insertBefore(countSpan, bubble.firstChild);
                }
                countSpan.textContent = itemCount;
            } else if (countSpan) {
                countSpan.remove();
            }

            if (visuallyHiddenSpan) {
                visuallyHiddenSpan.textContent = visuallyHiddenSpan.textContent.replace(
                    /\d+/,
                    itemCount
                );
            }
        });
    }

    injectCampaignElementsIntoRebuy() {
        const rebuyFlyout = document.querySelector(".rebuy-cart__flyout");
        if (!rebuyFlyout) {
            return;
        }

        const announcementBar = document.querySelector(".campaign-cart-announcement");
        const rewardBar = document.querySelector(".campaign-cart-reward");
        if (!announcementBar && !rewardBar) {
            return;
        }

        let campaignWrapper = rebuyFlyout.querySelector(".rebuy-cart__flyout-campaign");
        if (!campaignWrapper) {
            campaignWrapper = document.createElement("div");
            campaignWrapper.className = "rebuy-cart__flyout-campaign";

            const flyoutHeader = rebuyFlyout.querySelector(".rebuy-cart__flyout-header");
            if (flyoutHeader && flyoutHeader.parentNode) {
                flyoutHeader.parentNode.insertBefore(campaignWrapper, flyoutHeader.nextSibling);
            } else {
                const flyoutContent =
                    rebuyFlyout.querySelector(".rebuy-cart__flyout-content") || rebuyFlyout;
                flyoutContent.insertBefore(campaignWrapper, flyoutContent.firstChild);
            }
        }

        if (announcementBar && !campaignWrapper.contains(announcementBar)) {
            campaignWrapper.appendChild(announcementBar);
        }
        if (rewardBar && !campaignWrapper.contains(rewardBar)) {
            campaignWrapper.appendChild(rewardBar);
        }
    }
}

if (!customElements.get("atlas-campaign-timer")) {
    customElements.define("atlas-campaign-timer", AtlasCampaignTimer);
}
if (!customElements.get("atlas-campaign-pdp-entries")) {
    customElements.define("atlas-campaign-pdp-entries", AtlasCampaignPdpEntries);
}
if (!customElements.get("atlas-campaign-cart")) {
    customElements.define("atlas-campaign-cart", AtlasCampaignCart);
}
